import { CloseIcon, Dialog, Flex, ProviderConsumer } from "@fluentui/react-northstar";
import React, { useState } from 'react';
import { AppStateValue } from "../../../../AppState";
import { mergeStyleSets } from '@fluentui/react';
import { themeNames } from '@fluentui/react-teams';
import { useTranslation } from 'react-i18next';
import MgtCss from '../../../../../../src/components/mgt.module.css';
import { AdaptiveCardElement } from '../../../../templates/AdaptiveCardElement';
import { CustomFormUploadProps, parseCard } from '../../TicketTemplateAnswer';
import { CustomForm, CustomFormAttachmentsProps } from '../../../../templates/CustomForms/CustomForm.Interface';
import { parseIfJSON } from '../../../utils/helper';
import { FileProps } from '../../EditTicketAttachments/EditTicketAttachments';
import { NewAttachmentFlowContent } from '../../EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';

const classNames = mergeStyleSets({
  customFormDialog: {
    '.ac-textBlock': {
      padding: '5px 0 !important'
    }
  },
  customForm: {
    background: 'var(--mgt-theme-background) !important',
    '.ac-textBlock': {
      padding: '5px 0 !important',
      color: 'var(--mgt-theme-foreground) !important'
    },
    '.ac-richTextBlock > span': {
      color: 'var(--mgt-theme-foreground) !important'
    },
    '.ac-pushButton': {
      background: 'var(--mgt-theme-brand-background) !important'
    }
  },
  customFormDisabledDefault: {
    '.tikit-typeahead': {
      '--selected-container-background-color': 'rgb(240, 240, 240) !important',
    },
    '.tikit-cardPicker': {
      '--input-background-color': 'rgb(240, 240, 240) !important',
    },
    '.ac-pushButton,.ac-input,.ac-input.ac-multichoiceInput.ac-choiceSetInput-compact': {
      background: 'rgb(240, 240, 240) !important',
    }
  },
  customFormDisabledDark: {
    '.ac-input.ac-textInput, .ac-input.ac-multichoiceInput.ac-choiceSetInput-compact': {
      background: 'rgb(35, 35, 35) !important',
      color: 'rgb(198, 198, 198) !important'
    },
    '.ac-pushButton': {
      background: '#1f1f1f !important',
    }
  },
  customFormDisabledRoot: {
    '.ac-pushButton': {
      pointerEvents: 'none !important',
      cursor: 'default !important'
    },
    '.ac-richTextBlock': {
      pointerEvents: 'none !important',
      cursor: 'default !important'
    },
    '.ac-input': {
      pointerEvents: 'none !important',
      cursor: 'default !important'
    }
  },
  customFormFileLinkDisabled: {
    '.tikit-card-files': {
      color: 'var(--mgt-theme-default-foreground2) !important'
    }
  }
});

interface CFAdaptiveCardProps {
  customForm: any;
  disableForm: boolean;
  onInputValueChanged: any;
  currentState: AppStateValue;
  isEndUserPage: boolean;
  templateFormCount: number;
  dispatchEvent?: any;
}

export const TicketCustomFormAdaptiveCard = ({ customForm, disableForm, onInputValueChanged, currentState, isEndUserPage, templateFormCount, dispatchEvent }: CFAdaptiveCardProps) => {
  const { t } = useTranslation();
  const [customFormAttachmentsProps, setCustomFormAttachmentsProps] = useState<CustomFormAttachmentsProps>({} as CustomFormAttachmentsProps);
  const [openUploadFileDialog, setOpenUploadFileDialog] = useState(false);
  const [uploadedFiles] = useState<File[]>([]);
  const [currentCustomForm] = useState(customForm);

  const parseThisCardTemplate = (currentForm: CustomForm) => {
    let templateCardJSON = parseIfJSON(currentForm.CardTemplateJson);
    let templateAnswerJSON = parseIfJSON(currentForm.CardAnswerJson);

    if (isEndUserPage) {
      if (!(parseIfJSON(templateAnswerJSON["Ticket.AddComment"] ?? "{}")?.IsPublic ?? true))
        templateAnswerJSON["Ticket.AddComment"] = "{ \"Body\": \"\", \"IsPublic\": true }";

      if (!(templateAnswerJSON["Ticket.UploadFile"]?.Comment?.IsPublic ?? true))
        templateAnswerJSON["Ticket.UploadFile"] = { Comment: { Body: "", IsPublic: true }, Files: [] };

      const indices = templateCardJSON.body.reduce((acc, x, i) => {
        if (x.id === "Ticket.AddComment") acc.push(i);
        return acc;
      }, [] as number[]);

      indices.forEach(i => { templateCardJSON.body[i].showPrivacy = false; });
    }

    const extraProps: CustomFormUploadProps = {
      activeFormTemplateId: currentForm?.TemplateFormId
    };

    if (!customFormAttachmentsProps?.TemplateFormId)
      extraProps.setCustomFormAttachmentsProps = setCustomFormAttachmentsProps;

    parseCard(templateCardJSON.body, templateAnswerJSON, currentState, extraProps);
    return JSON.stringify(templateCardJSON);
  }

  const onOpenUploadFromCardAction = () => {
    setOpenUploadFileDialog(!openUploadFileDialog);
  }

  const onCloseUploadFromCardAction = (currentFiles?: FileProps[], optionalMessage?: string, isPrivate?: boolean) => {
    setOpenUploadFileDialog(false);
    if (currentFiles?.length > 0) {

      const newFormAttachmentProps: CustomFormAttachmentsProps = { TemplateFormId: currentCustomForm?.TemplateFormId, Files: currentFiles, Message: optionalMessage, IsPrivate: isPrivate };
      setCustomFormAttachmentsProps(newFormAttachmentProps);
      if (dispatchEvent)
        dispatchEvent([newFormAttachmentProps]);
    }
  }

  return (<ProviderConsumer
    render={(globalTheme) => (
      <>
        <Flex key={customForm.Id} column gap="gap.small" className={`mt-2 custom-form-answer`}>
          <AdaptiveCardElement
            jsonString={parseThisCardTemplate(currentCustomForm)}
            cardProps={{ styles: { height: 'fit-content', widht: '100%' } }}
            containerClassName={`${classNames.customForm} 
              ${!disableForm && classNames.customForm}
              ${(disableForm) && MgtCss.customFormDisabledRoot}
              ${(disableForm && globalTheme.siteVariables.theme === themeNames.Dark) && classNames.customFormDisabledDark} 
              ${(disableForm && globalTheme.siteVariables.theme === themeNames.Default) && classNames.customFormDisabledDefault} 
            
              ${disableForm && classNames.customFormFileLinkDisabled}
            `}
            onInputValueChanged={onInputValueChanged}
            applyCustomVisibility={!isEndUserPage}
            uploadFileProps={{ key: "TicketCustomFormSection", onOpenUploadFromCardAction: onOpenUploadFromCardAction, currentFiles: customFormAttachmentsProps?.Files, message: customFormAttachmentsProps?.Message, messageIsPrivate: customFormAttachmentsProps?.IsPrivate }}
            isEndUserPage={isEndUserPage}
            isReadOnly={disableForm || false}
            globalTheme={globalTheme}
          />
          <Dialog
            content={<NewAttachmentFlowContent globalTheme={globalTheme} ticketId={0} filesToUpload={uploadedFiles} onInteraction={() => { }} teamsConfig={currentState.teamsConfig} isEndUserPage={isEndUserPage} onClose={onCloseUploadFromCardAction} uploadFromCustomFormProps={{ fromTemplate: true, attachToCustomFormFromWeb: true }} />}
            header={t('ticket-details.edit-ticket-form.custom-form-answer.addto-customform')}
            headerAction={{
              icon: <CloseIcon />,
              title: t('common.buttons.close'),
              onClick: () => setOpenUploadFileDialog(false)
            }}
            style={{ maxWidth: '700px' }}
            className="responsive-dialog top-0 fixed md:relative z-10 mobile-full-p md:w-attachment"
            closeOnOutsideClick={false}
            open={openUploadFileDialog}
          />
        </Flex>

      </>
    )}
  />
  )
}
